import { attachPayerWithRetry, PaymentSessionClient } from './api/client';
import { createEmbedHost, hideModal, showModal, type EmbedHost } from './embed/host';
import { isTerminalStatus, SessionPoller } from './session/poller';
import { msUntil } from './session/timing';
import { applyThemeToHost } from './theme';
import { CheckoutUi } from './ui/checkout-ui';
import type {
  ApiCheckoutMethod,
  CheckoutMethod,
  CheckoutPhase,
  CheckoutSurface,
  PayerPreview,
  PaymentSession,
  ZippyPayCheckoutInstance,
  ZippyPayConfig,
  ZippyPayError,
} from './types';
import { ZippyPayError as ZippyPayErrorClass } from './types';
import { parseZippyEnvironment, resolveApiBaseUrl } from './config/environments';
import { openAppStore, openPaymentDeepLink } from './qr/deep-link';
import {
  buildBindPolicyRequest,
  isCheckoutPolicyBound,
  isCheckoutStartAllowed,
  resolveAutoStartMethod,
  resolveCheckoutUiOptions,
  resolveCheckoutUiOptionsFromBound,
  resolveCheckoutUiOptionsFromSession,
  resolveEntryRouting,
  type ResolvedCheckoutUiOptions,
} from './config/checkout-policy';
import {
  detectCheckoutSurface,
  subscribeCheckoutSurface,
} from './config/device';
import {
  type AppHandoffStatus,
  watchAppHandoff,
} from './app/handoff';
import {
  isUuidV4,
  isValidPayerQuery,
  normalizePayerQuery,
  payerQueryValidationMessage,
  appendRedirectQueryParams,
  type TerminalExitMode,
} from './utils';

export class CheckoutController implements ZippyPayCheckoutInstance {
  private readonly config: ZippyPayConfig;
  private readonly client: PaymentSessionClient;
  private readonly embed: EmbedHost;
  private readonly poller: SessionPoller;
  private ui: CheckoutUi | null = null;
  private session: PaymentSession | null = null;
  private paymentId = '';
  private verifiedPayer: PayerPreview | null = null;
  private verifyMatches: PayerPreview[] | null = null;
  private lastVerifiedQuery: string | null = null;
  private committedMethod: CheckoutMethod | null = null;
  private phase: CheckoutPhase = 'loading';
  private errorMessage: string | null = null;
  private loading = true;
  private destroyed = false;
  private modalOpen = false;
  private exitMode: TerminalExitMode | null = null;
  private terminalFailureReason: 'expired' | 'invalid-session' | 'failed' | 'cancelled' | 'declined' | null = null;
  private uiOptions: ResolvedCheckoutUiOptions;
  private appHandoffStatus: AppHandoffStatus = 'idle';
  private clearAppHandoffWatch: (() => void) | null = null;
  private methodSwitchInProgress = false;
  private entryRoutingDone = false;
  private payWindowExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeSurface: (() => void) | null = null;

  private static readonly PAY_WINDOW_PHASES: readonly CheckoutPhase[] = [
    'qr-active',
    'mobile-app-active',
    'awaiting',
    'manual-confirm',
  ];

  constructor(config: ZippyPayConfig) {
    this.config = {
      mode: 'inline',
      closeOnBackdropClick: false,
      closeOnEscape: false,
      ...config,
    };

    if (!isUuidV4(this.config.sessionId)) {
      throw new Error('Zippy Pay: sessionId must be a UUID v4.');
    }

    parseZippyEnvironment(this.config.environment);

    this.uiOptions = resolveCheckoutUiOptions(
      this.config.checkoutPolicy,
      this.config.mobileApp,
      detectCheckoutSurface(),
    );

    this.client = new PaymentSessionClient({
      baseUrl: resolveApiBaseUrl(this.config.environment),
      sessionId: this.config.sessionId,
      clientToken: this.config.clientToken,
    });

    this.embed = createEmbedHost(this.config);
    applyThemeToHost(this.embed.host, this.config.theme);

    this.poller = new SessionPoller(() => this.client.getSession(), {
      onUpdate: (session) => this.handleSessionUpdate(session),
      onError: (error) => this.handlePollError(error),
    });

    this.ui = new CheckoutUi(
      this.embed.host,
      this.snapshotUiState(),
      {
        onChooseMethod: (method) => {
          void this.chooseMethod(method);
        },
        onVerify: (query) => this.verify(query),
        onPay: (paymentId) => this.attach(paymentId),
        onSelectPayer: (payer) => this.selectPayer(payer),
        onBackToChooseMethod: () => this.backToChooseMethod(),
        onBackToManualEntry: () => this.backToManualEntry(),
        onBackToMobileAppFallback: () => this.backToMobileAppFallback(),
        onOpenMobileApp: () => this.beginMobileAppHandoff(),
        onInstallZippy: () => this.installZippy(),
        onRetryMobileApp: () => this.beginMobileAppHandoff(),
        onPaymentIdChange: (query) => {
          const trimmed = normalizePayerQuery(query);
          let shouldSync = false;

          if (
            (this.verifiedPayer || this.verifyMatches) &&
            this.lastVerifiedQuery !== null &&
            trimmed !== this.lastVerifiedQuery
          ) {
            this.verifiedPayer = null;
            this.verifyMatches = null;
            this.lastVerifiedQuery = null;
            if (
              this.phase === 'manual-confirm' ||
              this.phase === 'manual-picker'
            ) {
              this.phase = 'manual-entry';
            }
            shouldSync = true;
          }

          if (this.errorMessage) {
            this.errorMessage = null;
            shouldSync = true;
          }

          this.paymentId = query;

          if (shouldSync) {
            this.syncUi();
          }
        },
        onTerminalExit: () => this.executeTerminalExit(),
      },
      this.uiOptions,
    );

    void this.bootstrap();

    this.unsubscribeSurface = subscribeCheckoutSurface((surface) => {
      this.handleSurfaceChange(surface);
    });

    if (this.config.mode === 'inline') {
      this.modalOpen = true;
    }
  }

  open(): void {
    if (this.config.mode !== 'modal' || this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    showModal(this.embed, this.config, () => this.close());
  }

  close(): void {
    if (this.config.mode !== 'modal') {
      return;
    }
    this.modalOpen = false;
    hideModal(this.embed);
    this.config.onClose?.();
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.unsubscribeSurface?.();
    this.unsubscribeSurface = null;
    this.poller.stop();
    this.clearAppHandoffWatch?.();
    this.clearAppHandoffWatch = null;
    this.clearPayWindowExpiryWatch();
    this.ui?.destroy();
    this.embed.destroy();
    this.ui = null;
  }

  private snapshotUiState() {
    return {
      phase: this.phase,
      session: this.session,
      sessionId: this.config.sessionId,
      committedMethod: this.committedMethod,
      paymentId: this.paymentId,
      verifiedPayer: this.verifiedPayer,
      verifyMatches: this.verifyMatches,
      errorMessage: this.errorMessage,
      loading: this.loading,
      mode: this.config.mode ?? 'inline',
      exitMode: this.exitMode,
      appHandoffStatus: this.appHandoffStatus,
      mobileAppManualFallback: this.isMobileAppManualFallbackActive(),
    };
  }

  private syncUi(partial?: {
    errorMessage?: string | null;
    loading?: boolean;
    exitMode?: TerminalExitMode | null;
  }): void {
    if (this.destroyed) {
      return;
    }

    if (partial && 'errorMessage' in partial) {
      this.errorMessage = partial.errorMessage ?? null;
    }
    if (partial && 'loading' in partial) {
      this.loading = partial.loading ?? false;
    }
    if (partial && 'exitMode' in partial) {
      this.exitMode = partial.exitMode ?? null;
    }

    this.ui?.update(this.snapshotUiState());
  }

  private async bootstrap(): Promise<void> {
    try {
      const session = await this.bindPolicy();
      if (this.destroyed) {
        return;
      }

      this.applyBoundUiOptions(session);
      this.applySession(session);
      if (this.destroyed) {
        return;
      }

      this.config.onReady?.(session);

      if (!isTerminalStatus(session.status)) {
        await this.routeIdleEntry();
        if (!this.destroyed) {
          this.syncUi({ loading: false });
          this.poller.start(session);
        }
      } else if (!this.destroyed) {
        this.syncUi({ loading: false });
      }
    } catch (error) {
      this.handleBootstrapError(error);
    }
  }

  private async bindPolicy(): Promise<PaymentSession> {
    const bindRequest = buildBindPolicyRequest({
      checkoutPolicy: this.config.checkoutPolicy,
      mobileApp: this.config.mobileApp,
    });

    try {
      return await this.client.bindCheckoutPolicy(bindRequest);
    } catch (error) {
      if (
        error instanceof ZippyPayErrorClass &&
        error.code === 'payment-session.checkout-policy-conflict'
      ) {
        const session = await this.client.getSession();
        if (isCheckoutPolicyBound(session)) {
          return session;
        }
      }
      throw error;
    }
  }

  private applyBoundUiOptions(session: PaymentSession, surface = detectCheckoutSurface()): void {
    this.uiOptions = resolveCheckoutUiOptionsFromSession(session, surface);
    this.ui?.updateUiOptions(this.uiOptions);
  }

  private handleSurfaceChange(surface: CheckoutSurface): void {
    if (this.destroyed || this.committedMethod) {
      return;
    }

    const session = this.session;
    if (!session || !isCheckoutPolicyBound(session)) {
      return;
    }

    if (this.uiOptions.surface === surface) {
      return;
    }

    this.uiOptions = resolveCheckoutUiOptionsFromBound(
      session.checkoutPolicy,
      session.mobileAppPolicy ?? 'OFF',
      surface,
    );
    this.ui?.updateUiOptions(this.uiOptions);

    if (this.phase === 'choose-method' || this.phase === 'loading') {
      this.syncUi();
    }
  }

  private applySession(
    session: PaymentSession,
    options?: { fromMethodSwitch?: boolean },
  ): void {
    this.session = session;
    this.config.onSessionUpdate?.(session);

    if (session.transactionStatus === 'DECLINED' && session.status !== 'COMPLETED') {
      this.methodSwitchInProgress = false;
      this.handleFailure({ reason: 'declined', session });
      return;
    }

    if (session.status === 'COMPLETED') {
      this.methodSwitchInProgress = false;
      this.handleSuccess(session);
      return;
    }

    if (session.status === 'EXPIRED') {
      this.methodSwitchInProgress = false;
      this.handleFailure({ reason: 'expired', session });
      return;
    }

    if (session.status === 'FAILED') {
      this.methodSwitchInProgress = false;
      this.handleFailure({ reason: 'failed', session });
      return;
    }

    if (session.status === 'CANCELLED') {
      this.methodSwitchInProgress = false;
      this.handleFailure({ reason: 'cancelled', session });
      return;
    }

    if (this.isMobileAppManualFallbackActive() && !options?.fromMethodSwitch) {
      this.schedulePayWindowExpiryWatch();
      this.syncUi();
      return;
    }

    this.committedMethod = uiMethodFromApi(session.checkoutMethod) ?? this.committedMethod;

    this.phase = this.resolveActivePhase(session);
    this.schedulePayWindowExpiryWatch();
    this.syncUi();
  }

  private schedulePayWindowExpiryWatch(): void {
    this.clearPayWindowExpiryWatch();

    const session = this.session;
    if (!session?.paymentExpiryAt || !CheckoutController.PAY_WINDOW_PHASES.includes(this.phase)) {
      return;
    }

    const remainingMs = msUntil(session.paymentExpiryAt);
    if (remainingMs <= 0) {
      void this.handlePayWindowExpired();
      return;
    }

    this.payWindowExpiryTimer = setTimeout(() => {
      void this.handlePayWindowExpired();
    }, remainingMs + 250);
  }

  private clearPayWindowExpiryWatch(): void {
    if (this.payWindowExpiryTimer !== null) {
      clearTimeout(this.payWindowExpiryTimer);
      this.payWindowExpiryTimer = null;
    }
  }

  private async handlePayWindowExpired(): Promise<void> {
    if (this.destroyed || !this.session?.paymentExpiryAt) {
      return;
    }

    if (msUntil(this.session.paymentExpiryAt) > 0) {
      this.schedulePayWindowExpiryWatch();
      return;
    }

    if (!CheckoutController.PAY_WINDOW_PHASES.includes(this.phase)) {
      return;
    }

    try {
      const session = await this.client.getSession();
      if (this.destroyed) {
        return;
      }
      this.applySession(session);
    } catch {
      if (!this.destroyed) {
        this.handleFailure({ reason: 'expired', session: this.session });
      }
    }
  }

  private resolveActivePhase(session: PaymentSession): CheckoutPhase {
    if (session.status === 'AWAITING_PAYMENT') {
      if (
        session.checkoutMethod === 'MOBILE_APP' ||
        this.committedMethod === 'mobileApp'
      ) {
        return 'mobile-app-active';
      }
      if (session.checkoutMethod === 'QR' || this.committedMethod === 'qr') {
        return 'qr-active';
      }
      return 'awaiting';
    }

    if (session.phase === 'PAYING' && session.checkoutMethod === 'MOBILE_APP') {
      return 'mobile-app-active';
    }

    if (session.phase === 'PAYING' && session.checkoutMethod === 'QR') {
      return 'qr-active';
    }

    if (session.phase === 'PAYING' && session.checkoutMethod === 'MANUAL') {
      if (this.phase === 'manual-confirm' && this.verifiedPayer) {
        return 'manual-confirm';
      }
      return 'manual-entry';
    }

    if (session.phase === 'IDLE') {
      if (this.phase === 'manual-confirm' && this.verifiedPayer) {
        return 'manual-confirm';
      }
      if (this.phase === 'manual-picker' && this.verifyMatches) {
        return 'manual-picker';
      }
      if (this.phase === 'manual-entry') {
        return 'manual-entry';
      }
      if (this.phase === 'choose-method') {
        return 'choose-method';
      }
      if (this.phase === 'qr-active' && this.committedMethod === 'qr') {
        return 'qr-active';
      }
      if (this.phase === 'mobile-app-active' && this.committedMethod === 'mobileApp') {
        return 'mobile-app-active';
      }
      if (!session.checkoutMethod) {
        const routing = resolveEntryRouting(
          this.uiOptions.checkoutPolicy,
          this.uiOptions.mobileAppPolicy,
          this.uiOptions.surface,
        );
        if (routing !== 'chooser') {
          return 'loading';
        }
      }
      return 'choose-method';
    }

    return this.phase;
  }

  private handleSessionUpdate(session: PaymentSession): void {
    if (this.destroyed) {
      return;
    }

    this.applySession(session);
  }

  private handleFailure(params: {
    reason: 'expired' | 'invalid-session' | 'failed' | 'cancelled' | 'declined';
    session?: PaymentSession | null;
    error?: ZippyPayError;
  }): void {
    if (this.destroyed) {
      return;
    }

    this.poller.stop();
    this.clearPayWindowExpiryWatch();
    this.loading = false;

    const session = params.session ?? this.session;

    if (params.reason === 'expired') {
      if (session) {
        this.config.onExpired?.(session);
      }
    } else {
      const error =
        params.error ??
        (params.reason === 'failed' ||
        params.reason === 'cancelled' ||
        params.reason === 'declined'
          ? terminalSessionError(params.reason)
          : undefined);
      if (error) {
        this.config.onError?.(error);
      }
    }

    this.terminalFailureReason = params.reason;
    this.phase = this.failurePhaseFromReason(params.reason);
    this.exitMode = this.resolveExitMode('failure', session);
    this.syncUi();
  }

  private handleSuccess(session: PaymentSession): void {
    if (this.destroyed) {
      return;
    }

    this.poller.stop();
    this.clearPayWindowExpiryWatch();
    this.loading = false;
    this.config.onComplete?.(session);

    this.terminalFailureReason = null;
    this.phase = 'success';
    this.exitMode = this.resolveExitMode('success', session);
    this.syncUi();
  }

  private executeTerminalExit(): void {
    if (this.destroyed) {
      return;
    }

    const session = this.session;

    if (this.phase === 'success' && session) {
      if (this.config.redirectOnComplete && session.successUrl) {
        window.location.assign(
          appendRedirectQueryParams(session.successUrl, {
            sessionId: session.sessionId,
            status: session.status,
            transactionId: session.transactionId,
          }),
        );
        return;
      }
    } else if (
      this.phase === 'expired' ||
      this.phase === 'failed' ||
      this.phase === 'cancelled' ||
      this.phase === 'declined' ||
      this.phase === 'invalid-session'
    ) {
      const failureUrl = session?.failureUrl ?? null;
      if (this.config.redirectOnFailure && failureUrl) {
        const redirectUrl =
          this.terminalFailureReason === 'expired'
            ? appendRedirectQueryParams(failureUrl, {
                sessionId: session?.sessionId ?? this.config.sessionId,
                status: session?.status ?? 'EXPIRED',
              })
            : failureUrl;
        window.location.assign(redirectUrl);
        return;
      }
    }

    this.teardownView();
  }

  private resolveExitMode(
    kind: 'success' | 'failure',
    session: PaymentSession | null | undefined,
  ): TerminalExitMode {
    if (kind === 'success') {
      return this.config.redirectOnComplete && session?.successUrl ? 'redirect' : 'close';
    }
    return this.config.redirectOnFailure && session?.failureUrl ? 'redirect' : 'close';
  }

  private failurePhaseFromReason(
    reason: 'expired' | 'invalid-session' | 'failed' | 'cancelled' | 'declined',
  ): CheckoutPhase {
    switch (reason) {
      case 'expired':
        return 'expired';
      case 'failed':
        return 'failed';
      case 'cancelled':
        return 'cancelled';
      case 'declined':
        return 'declined';
      case 'invalid-session':
        return 'invalid-session';
    }
  }

  private teardownView(): void {
    if (this.config.mode === 'modal' && this.modalOpen) {
      this.close();
    }
    this.destroy();
  }

  private handleBootstrapError(error: unknown): void {
    if (error instanceof ZippyPayErrorClass && error.status === 401) {
      this.handleFailure({
        reason: 'invalid-session',
        error,
        session: this.session,
      });
      return;
    }
    this.handleError(error);
  }

  private handlePollError(error: unknown): void {
    const normalized = normalizeError(error);
    if (normalized.status === 401) {
      this.poller.stop();
      this.handleFailure({
        reason: 'invalid-session',
        error: normalized,
        session: this.session,
      });
      return;
    }
    if (normalized.code === 'payment-session.not-attachable') {
      this.poller.stop();
      this.handleFailure({ reason: 'expired', session: this.session });
      return;
    }
  }

  private handleError(error: unknown): void {
    const normalized = normalizeError(error);
    if (normalized.status === 401) {
      this.handleFailure({
        reason: 'invalid-session',
        error: normalized,
        session: this.session,
      });
      return;
    }
    if (normalized.code === 'payment-session.not-attachable') {
      this.handleFailure({ reason: 'expired', session: this.session });
      return;
    }
    if (normalized.code === 'payment-session.checkout-policy-not-bound') {
      if (this.phase === 'loading' || this.phase === 'choose-method') {
        this.phase = 'error';
      }
      this.syncUi({
        errorMessage:
          normalized.detail || 'Checkout is not ready yet. Please refresh and try again.',
        loading: false,
      });
      this.config.onError?.(normalized);
      this.poller.stop();
      return;
    }
    if (this.phase === 'loading' || this.phase === 'choose-method') {
      this.phase = 'error';
    }
    this.syncUi({ errorMessage: normalized.detail, loading: false });
    this.config.onError?.(normalized);
    this.poller.stop();
  }

  private async routeIdleEntry(): Promise<void> {
    if (this.destroyed || this.entryRoutingDone) {
      return;
    }

    const session = this.session;
    if (!session || isTerminalStatus(session.status)) {
      return;
    }

    if (session.checkoutMethod) {
      this.entryRoutingDone = true;
      return;
    }

    if (session.phase !== 'IDLE') {
      return;
    }

    const surface = detectCheckoutSurface();
    const { checkoutPolicy, mobileAppPolicy } = this.uiOptions;
    const autoMethod = resolveAutoStartMethod(checkoutPolicy, mobileAppPolicy, surface);

    if (autoMethod === 'MOBILE_APP') {
      await this.startMobileAppFlow(false);
    } else if (autoMethod === 'QR') {
      await this.chooseMethod('qr');
    } else {
      const routing = resolveEntryRouting(checkoutPolicy, mobileAppPolicy, surface);
      if (routing === 'auto-manual') {
        this.phase = 'manual-entry';
        this.syncUi({ loading: false });
      } else {
        this.phase = 'choose-method';
        this.syncUi({ loading: false });
      }
    }

    this.entryRoutingDone = true;
  }

  private openMobileApp(): void {
    openPaymentDeepLink(this.config.sessionId);
  }

  private beginMobileAppHandoff(): void {
    if (this.destroyed) {
      return;
    }

    this.clearAppHandoffWatch?.();
    this.clearAppHandoffWatch = null;
    this.appHandoffStatus = 'opening';
    this.syncUi();

    this.openMobileApp();

    this.clearAppHandoffWatch = watchAppHandoff({
      onOpened: () => {
        if (this.destroyed) {
          return;
        }
        this.appHandoffStatus = 'opened';
        this.syncUi();
      },
      onFailed: () => {
        if (this.destroyed) {
          return;
        }
        this.appHandoffStatus = 'failed';
        this.syncUi();
      },
    });
  }

  private installZippy(): void {
    openAppStore();
  }

  private async startMobileAppFlow(attemptOpen: boolean): Promise<void> {
    if (this.session?.checkoutMethod === 'MOBILE_APP') {
      this.committedMethod = 'mobileApp';
      this.phase = 'mobile-app-active';
      if (attemptOpen) {
        this.beginMobileAppHandoff();
      }
      this.syncUi({ loading: false });
      return;
    }

    if (this.committedMethod && this.committedMethod !== 'mobileApp') {
      return;
    }

    const surface = detectCheckoutSurface();
    if (!this.canStartAppClaim('MOBILE_APP', surface)) {
      return;
    }

    if (attemptOpen) {
      this.committedMethod = 'mobileApp';
      this.phase = 'mobile-app-active';
      this.beginMobileAppHandoff();
    } else {
      this.syncUi({ errorMessage: null, loading: true });
    }

    try {
      const session = await this.client.startMobileAppCheckout(surface);
      this.committedMethod = 'mobileApp';
      this.applySession(session);
      if (this.destroyed) {
        return;
      }

      this.phase = 'mobile-app-active';
      this.syncUi({ loading: false });
    } catch (error) {
      this.handleFlowError(error, 'choose-method');
    }
  }

  private canStartAppClaim(
    method: Extract<ApiCheckoutMethod, 'QR' | 'MOBILE_APP'>,
    surface: CheckoutSurface,
  ): boolean {
    if (!this.session || !isCheckoutPolicyBound(this.session)) {
      this.syncUi({
        errorMessage: 'Checkout is not ready yet. Please refresh and try again.',
        loading: false,
      });
      return false;
    }

    const checkoutPolicy = this.session.checkoutPolicy;
    const mobileAppPolicy = this.session.mobileAppPolicy ?? 'OFF';

    if (!isCheckoutStartAllowed(checkoutPolicy, mobileAppPolicy, surface, method)) {
      this.syncUi({
        errorMessage: 'This payment option is not available on your device.',
        loading: false,
      });
      return false;
    }

    return true;
  }

  private async chooseMethod(method: CheckoutMethod): Promise<void> {
    if (this.methodSwitchInProgress) {
      return;
    }

    if (
      this.committedMethod &&
      !(
        this.appHandoffStatus === 'failed' &&
        this.committedMethod === 'mobileApp' &&
        method !== 'mobileApp'
      )
    ) {
      return;
    }

    if (
      this.appHandoffStatus === 'failed' &&
      this.committedMethod === 'mobileApp' &&
      (method === 'qr' || method === 'manual')
    ) {
      await this.chooseFallbackMethod(method);
      return;
    }

    if (method === 'mobileApp') {
      await this.startMobileAppFlow(true);
      return;
    }

    this.verifiedPayer = null;
    this.syncUi({ errorMessage: null, loading: method === 'qr' });

    try {
      if (method === 'qr') {
        const surface = detectCheckoutSurface();
        if (!this.canStartAppClaim('QR', surface)) {
          return;
        }

        const session = await this.client.startQrCheckout(surface);
        this.committedMethod = 'qr';
        this.applySession(session);
        if (this.destroyed) {
          return;
        }
        this.syncUi({ loading: false });
      } else {
        this.phase = 'manual-entry';
        this.syncUi({ loading: false });
      }
    } catch (error) {
      this.handleFlowError(error, 'choose-method');
    }
  }

  private async chooseFallbackMethod(method: 'qr' | 'manual'): Promise<void> {
    if (
      this.appHandoffStatus !== 'failed' ||
      this.committedMethod !== 'mobileApp' ||
      this.methodSwitchInProgress
    ) {
      return;
    }

    this.clearAppHandoffWatch?.();
    this.clearAppHandoffWatch = null;

    if (method === 'manual') {
      // MOBILE_APP → MANUAL: attach auto-switches on the backend; no checkout/start.
      this.methodSwitchInProgress = true;
      this.appHandoffStatus = 'idle';
      this.verifiedPayer = null;
      this.verifyMatches = null;
      this.lastVerifiedQuery = null;
      this.paymentId = '';
      this.phase = 'manual-entry';
      this.syncUi({ errorMessage: null, loading: false });
      return;
    }

    this.methodSwitchInProgress = true;
    this.verifiedPayer = null;
    this.syncUi({ errorMessage: null, loading: true });

    try {
      const surface = detectCheckoutSurface();
      const session = await this.client.startQrCheckout(surface);
      if (this.destroyed) {
        return;
      }

      this.methodSwitchInProgress = false;
      this.appHandoffStatus = 'idle';
      this.loading = false;
      this.applySession(session, { fromMethodSwitch: true });
    } catch (error) {
      this.methodSwitchInProgress = false;
      this.handleFlowError(error, 'mobile-app-active');
    }
  }

  private backToChooseMethod(): void {
    if (!this.uiOptions.allowManualBack) {
      return;
    }

    if (this.committedMethod || (this.loading && this.verifiedPayer)) {
      return;
    }

    this.phase = 'choose-method';
    this.paymentId = '';
    this.verifiedPayer = null;
    this.verifyMatches = null;
    this.lastVerifiedQuery = null;
    this.errorMessage = null;
    this.loading = false;
    this.syncUi({ errorMessage: null, loading: false });
  }

  private backToManualEntry(): void {
    if (this.loading && this.verifiedPayer) {
      return;
    }

    this.phase = 'manual-entry';
    this.verifiedPayer = null;
    this.verifyMatches = null;
    this.lastVerifiedQuery = null;
    this.errorMessage = null;
    this.loading = false;
    this.syncUi({ errorMessage: null, loading: false });
  }

  private backToMobileAppFallback(): void {
    if (this.loading && this.verifiedPayer) {
      return;
    }

    if (!this.isMobileAppManualFallbackActive()) {
      return;
    }

    this.methodSwitchInProgress = false;
    this.appHandoffStatus = 'failed';
    this.phase = 'mobile-app-active';
    this.paymentId = '';
    this.verifiedPayer = null;
    this.verifyMatches = null;
    this.lastVerifiedQuery = null;
    this.errorMessage = null;
    this.loading = false;
    this.syncUi({ errorMessage: null, loading: false });
  }

  private isMobileAppManualFallbackActive(): boolean {
    if (this.committedMethod !== 'mobileApp') {
      return false;
    }

    if (this.methodSwitchInProgress) {
      return true;
    }

    return (
      this.session?.checkoutMethod === 'MOBILE_APP' &&
      (this.phase === 'manual-entry' ||
        this.phase === 'manual-picker' ||
        this.phase === 'manual-confirm')
    );
  }

  private selectPayer(payer: PayerPreview): void {
    if (this.loading || this.phase !== 'manual-picker') {
      return;
    }

    this.verifiedPayer = payer;
    this.phase = 'manual-confirm';
    this.errorMessage = null;
    this.syncUi({ errorMessage: null, loading: false });
  }

  private async verify(query: string): Promise<void> {
    this.paymentId = query;
    if (!isValidPayerQuery(query)) {
      this.syncUi({ errorMessage: payerQueryValidationMessage(), loading: false });
      return;
    }

    const normalizedQuery = normalizePayerQuery(query);
    this.syncUi({ errorMessage: null, loading: true });
    try {
      const { items } = await this.client.verifyPayer(query);
      if (this.phase !== 'manual-entry') {
        return;
      }

      this.lastVerifiedQuery = normalizedQuery;
      this.verifyMatches = null;

      if (items.length === 1) {
        this.verifiedPayer = items[0]!;
        this.phase = 'manual-confirm';
      } else {
        this.verifiedPayer = null;
        this.verifyMatches = items;
        this.phase = 'manual-picker';
      }

      this.syncUi({ loading: false });
    } catch (error) {
      this.handleFlowError(error, 'manual-entry');
    }
  }

  private async attach(_paymentId: string): Promise<void> {
    if (!this.verifiedPayer) {
      this.syncUi({
        errorMessage: 'Find your account before proceeding to pay.',
        loading: false,
      });
      return;
    }

    this.syncUi({ errorMessage: null, loading: true });
    try {
      const session = await attachPayerWithRetry(this.client, this.verifiedPayer.paymentId);
      this.methodSwitchInProgress = false;
      this.committedMethod = 'manual';
      this.applySession(session);
      if (this.destroyed) {
        return;
      }
      if (session.status === 'AWAITING_PAYMENT') {
        this.poller.start(session);
      }
      this.syncUi({ loading: false });
    } catch (error) {
      this.handleFlowError(error, 'manual-confirm');
    }
  }

  private handleFlowError(error: unknown, fallbackPhase: CheckoutPhase): void {
    const normalized = normalizeError(error);
    const message = formatFlowErrorMessage(normalized, fallbackPhase);

    if (normalized.code === 'payment-session.not-attachable') {
      this.handleFailure({ reason: 'expired', session: this.session });
      return;
    } else if (normalized.code === 'payment-session.checkout-method-conflict') {
      void this.refreshAfterConflict();
      this.syncUi({ errorMessage: message, loading: false });
      this.config.onError?.(normalized);
      return;
    } else if (normalized.code === 'payment-session.checkout-policy-not-bound') {
      this.syncUi({ errorMessage: message, loading: false });
      this.config.onError?.(normalized);
      return;
    } else if (
      normalized.code === 'payment-session.checkout-policy-denied' ||
      normalized.code === 'payment-session.checkout-policy-conflict'
    ) {
      void this.refreshAfterConflict();
      this.syncUi({ errorMessage: message, loading: false });
      this.config.onError?.(normalized);
      return;
    } else {
      this.phase = fallbackPhase;
    }

    this.syncUi({ errorMessage: message, loading: false });
    this.config.onError?.(normalized);
  }

  private async refreshAfterConflict(): Promise<void> {
    try {
      const session = await this.client.getSession();
      if (isCheckoutPolicyBound(session)) {
        this.applyBoundUiOptions(session);
      }
      this.applySession(session);
    } catch {
      // keep current error state
    }
  }
}

function terminalSessionError(reason: 'failed' | 'cancelled' | 'declined'): ZippyPayError {
  if (reason === 'failed') {
    return new ZippyPayErrorClass({
      code: 'payment-session.failed',
      status: 400,
      title: 'Payment failed',
      detail: 'Something went wrong with this payment.',
    });
  }

  if (reason === 'declined') {
    return new ZippyPayErrorClass({
      code: 'payment-session.declined',
      status: 400,
      title: 'Payment declined',
      detail: 'Your payment was declined.',
    });
  }

  return new ZippyPayErrorClass({
    code: 'payment-session.cancelled',
    status: 400,
    title: 'Payment cancelled',
    detail: 'This payment was cancelled.',
  });
}

function uiMethodFromApi(method: ApiCheckoutMethod | null): CheckoutMethod | null {
  if (method === 'QR') {
    return 'qr';
  }
  if (method === 'MANUAL') {
    return 'manual';
  }
  if (method === 'MOBILE_APP') {
    return 'mobileApp';
  }
  return null;
}

function formatFlowErrorMessage(error: ZippyPayError, phase: CheckoutPhase): string {
  const isVerifyFailure =
    phase === 'manual-entry' &&
    (error.status === 404 ||
      error.code.includes('not-found') ||
      error.code.includes('payer-not-found') ||
      error.code === 'payer.not-found');

  if (isVerifyFailure) {
    return 'No Zippy account found';
  }

  return error.detail?.trim() || 'Something went wrong. Please try again.';
}

function normalizeError(error: unknown): ZippyPayError {
  if (error instanceof ZippyPayErrorClass) {
    return error;
  }
  return new ZippyPayErrorClass({
    code: 'unknown',
    status: 500,
    title: 'Unexpected error',
    detail: error instanceof Error ? error.message : 'An unexpected error occurred',
  });
}

export function createCheckout(config: ZippyPayConfig): ZippyPayCheckoutInstance {
  return new CheckoutController(config);
}
